import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import { toast } from "react-toastify";

import { OrdenTrabajo } from "@/shared/interfaces";
import SubirFotosProcesoModal from "./SubirFotosProcesoModal";
import CompletarFotosTrasladoModal from "./traslado/CompletarFotosTrasladoModal";
import { SeccionFotos } from "./traslado/FotosTrasladoTab";
import {
  eliminarFotoTraslado,
  useAvisosFotosTraslado,
  useOrdenTrabajoFotosLive,
  useRefrescarFotosTrasladoLive,
  useSetFotosTrasladoLive,
} from "./traslado/fotos-traslado.actions";
import {
  EtapaFotoVisita,
  FOTOS_FINALIZACION_VISITA,
  FOTOS_INGRESO_VISITA,
  FOTOS_PROCESO_MAX_VISITA,
  FOTOS_PROCESO_MIN_VISITA,
  eliminadasFinalizacionVisitaDe,
  fotosFinalizacionVisitaDe,
  itemsIngresoVisitaDe,
  itemsProcesoVisitaDe,
  resumenFotosVisita,
  textoFotosFaltantesVisita,
} from "./fotos-visita.utils";

type FotoAEliminar = { etapa: EtapaFotoVisita; foto: any; titulo: string };

/**
 * Tab "Fotos" del flujo de VISITA — mismo mecanismo que el traslado
 * (FotosTrasladoTab): secciones Ingreso (2) / Proceso (2 a 5) / Finalización
 * (2 a 5, visible cuando la visita ya finalizó o en modo lectura).
 *
 * Modos:
 *  - Técnico (`readOnly=false`): sube fotos de proceso y VUELVE A SUBIR las que
 *    Activaciones haya eliminado (ingreso / proceso / finalización). Nunca
 *    elimina ni reemplaza.
 *  - Lectura (`readOnly`): vista de Activaciones, con resumen.
 *  - Activaciones (`readOnly` + `canDelete`): además puede ELIMINAR fotos; queda
 *    una marca y el técnico tiene que volver a subirlas.
 *
 * `live` (por defecto) sondea el estado de las fotos cada pocos segundos, así
 * lo que hace la otra parte se ve sin recargar la página.
 */
export default function FotosVisitaTab({
  ordenTrabajo,
  onRefresh,
  readOnly = false,
  canDelete = false,
  live = true,
  title,
}: {
  ordenTrabajo?: OrdenTrabajo;
  onRefresh?: () => void;
  readOnly?: boolean;
  canDelete?: boolean;
  live?: boolean;
  /** Encabezado opcional (p. ej. en Activaciones: "Evidencia fotográfica de la visita"). */
  title?: string;
}) {
  const [openProceso, setOpenProceso] = useState(false);
  const [completar, setCompletar] = useState<"INGRESO" | "FINALIZACION" | null>(null);
  const [fotoAEliminar, setFotoAEliminar] = useState<FotoAEliminar | null>(null);
  const [eliminando, setEliminando] = useState(false);

  // Fotos EN VIVO superpuestas sobre la OT recibida.
  const { ot, snapshot, enVivo } = useOrdenTrabajoFotosLive(ordenTrabajo, live, "visita");
  const setFotosLive = useSetFotosTrasladoLive();
  const refrescarFotosLive = useRefrescarFotosTrasladoLive();
  // Activaciones: aviso cuando el técnico sube fotos mientras revisa.
  useAvisosFotosTraslado(snapshot, canDelete ? "activaciones" : null);

  const idOT = (ot as any)?.id_orden_trabajo as string | undefined;
  const itemsIngreso = itemsIngresoVisitaDe(ot);
  const itemsProceso = itemsProcesoVisitaDe(ot);
  const itemsFinalizacion = [...fotosFinalizacionVisitaDe(ot), ...eliminadasFinalizacionVisitaDe(ot)];
  const resumen = resumenFotosVisita(ot);
  const porReponer = textoFotosFaltantesVisita(resumen);
  // Para eliminar se exige el estado en vivo: identifica la foto tal como está
  // guardada ahora mismo en la OT.
  const puedeEliminar = canDelete && enVivo && !!idOT;

  const pedirEliminar = (etapa: EtapaFotoVisita) =>
    puedeEliminar ? (foto: any, titulo: string) => setFotoAEliminar({ etapa, foto, titulo }) : undefined;

  const confirmarEliminar = async () => {
    if (!fotoAEliminar || !idOT) return;
    setEliminando(true);
    try {
      const nuevo = await eliminarFotoTraslado(
        idOT,
        {
          etapa: fotoAEliminar.etapa,
          photoUrl: fotoAEliminar.foto?.photoUrl,
          photoId: fotoAEliminar.foto?.photoId,
        },
        "visita",
      );
      setFotosLive(nuevo);
      toast.success("Foto eliminada. El técnico deberá volver a subirla.");
      onRefresh?.();
    } catch (e) {
      // El motivo ya lo mostró el cliente HTTP; se refresca por si la foto ya
      // no existía.
      console.error("Error al eliminar foto de visita", e);
      refrescarFotosLive(idOT, "visita");
    } finally {
      setEliminando(false);
      setFotoAEliminar(null);
    }
  };

  const evidenciaCompleta =
    !resumen.incompleto &&
    resumen.ingreso >= FOTOS_INGRESO_VISITA &&
    resumen.proceso >= FOTOS_PROCESO_MIN_VISITA &&
    (!resumen.finalizado || resumen.finalizacion >= FOTOS_FINALIZACION_VISITA);
  // Proceso: el técnico puede seguir cargando hasta el máximo; si Activaciones
  // eliminó alguna, primero se repone.
  const cupoProceso = resumen.faltanProceso + Math.max(0, FOTOS_PROCESO_MAX_VISITA - resumen.proceso - resumen.faltanProceso);
  const mostrarCargarProceso = !readOnly && cupoProceso > 0;
  // La finalización existe recién cuando el técnico cierra la visita.
  const mostrarFinalizacion = readOnly || resumen.finalizado || itemsFinalizacion.length > 0;

  return (
    <>
      {title && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <PhotoLibraryOutlinedIcon fontSize="small" sx={{ color: "#1565c0" }} />
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
        </Stack>
      )}
      <Grid container spacing={3} sx={{ p: 1 }}>
        {readOnly && (
          <Grid item xs={12}>
            <Alert severity={evidenciaCompleta ? "success" : "warning"} variant="outlined">
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" fontWeight={700}>
                  Evidencia del técnico:
                </Typography>
                <Chip size="small" label={`Ingreso ${resumen.ingreso}/${FOTOS_INGRESO_VISITA}`} />
                <Chip size="small" label={`Proceso ${resumen.proceso}/${FOTOS_PROCESO_MIN_VISITA}`} />
                <Chip size="small" label={`Finalización ${resumen.finalizacion}/${FOTOS_FINALIZACION_VISITA}`} />
                {enVivo && (
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    icon={<FiberManualRecordIcon sx={{ fontSize: 12 }} />}
                    label="En vivo"
                  />
                )}
              </Stack>
              {resumen.incompleto && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Pendiente de que el técnico vuelva a subir: <b>{porReponer}</b>.
                </Typography>
              )}
              {canDelete && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Si una foto no sirve puedes eliminarla: el técnico la verá como faltante y deberá
                  volver a subirla. Los cambios se reflejan solos, sin recargar la página.
                </Typography>
              )}
            </Alert>
          </Grid>
        )}

        {!readOnly && resumen.incompleto && (
          <Grid item xs={12}>
            <Alert severity="warning" variant="outlined">
              <Typography variant="body2" fontWeight={700}>
                Activaciones eliminó fotos de esta visita.
              </Typography>
              <Typography variant="body2">Debes volver a subir: {porReponer}.</Typography>
            </Alert>
          </Grid>
        )}

        <SeccionFotos
          titulo="Fotos de ingreso"
          items={itemsIngreso}
          esperadas={FOTOS_INGRESO_VISITA}
          onDelete={pedirEliminar("INGRESO")}
          action={
            !readOnly && resumen.faltanIngreso > 0 ? (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<AddPhotoAlternateIcon />}
                onClick={() => setCompletar("INGRESO")}
              >
                Volver a subir ({resumen.faltanIngreso})
              </Button>
            ) : undefined
          }
        />
        <SeccionFotos
          titulo="Fotos de proceso"
          items={itemsProceso}
          esperadas={FOTOS_PROCESO_MIN_VISITA}
          onDelete={pedirEliminar("PROCESO")}
          action={
            !readOnly ? (
              <Button
                size="small"
                variant="outlined"
                color={resumen.faltanProceso > 0 ? "warning" : "primary"}
                startIcon={<AddPhotoAlternateIcon />}
                onClick={() => setOpenProceso(true)}
                disabled={!mostrarCargarProceso}
              >
                {resumen.faltanProceso > 0
                  ? `Volver a subir (${resumen.faltanProceso})`
                  : mostrarCargarProceso
                    ? "Subir fotos de proceso"
                    : `Máximo alcanzado (${FOTOS_PROCESO_MAX_VISITA})`}
              </Button>
            ) : undefined
          }
        />
        {mostrarFinalizacion && (
          <SeccionFotos
            titulo="Fotos de finalización"
            items={itemsFinalizacion}
            esperadas={FOTOS_FINALIZACION_VISITA}
            onDelete={pedirEliminar("FINALIZACION")}
            action={
              !readOnly && resumen.faltanFinalizacion > 0 ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<AddPhotoAlternateIcon />}
                  onClick={() => setCompletar("FINALIZACION")}
                >
                  Volver a subir ({resumen.faltanFinalizacion})
                </Button>
              ) : undefined
            }
          />
        )}
      </Grid>

      {!readOnly && (
        <SubirFotosProcesoModal
          open={openProceso}
          onClose={() => setOpenProceso(false)}
          onSuccess={() => onRefresh?.()}
          ordenTrabajo={ot}
        />
      )}

      {!readOnly && completar && (
        <CompletarFotosTrasladoModal
          open={!!completar}
          onClose={() => setCompletar(null)}
          onSuccess={() => onRefresh?.()}
          ordenTrabajo={ot}
          etapa={completar}
          faltan={completar === "INGRESO" ? resumen.faltanIngreso : resumen.faltanFinalizacion}
          flujo="visita"
        />
      )}

      {/* Confirmación de borrado (solo Activaciones). */}
      <Dialog
        open={!!fotoAEliminar}
        onClose={eliminando ? undefined : () => setFotoAEliminar(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Eliminar foto de la visita</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Vas a eliminar <b>{fotoAEliminar?.titulo}</b>. El técnico la verá como faltante y deberá
            volver a subirla para completar la evidencia. Esta acción no se puede deshacer.
          </Typography>
          {fotoAEliminar?.foto?.photoUrl && (
            <Box
              component="img"
              src={fotoAEliminar.foto.photoUrl}
              alt={fotoAEliminar.titulo}
              sx={{
                width: "100%",
                maxHeight: 220,
                objectFit: "contain",
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "divider",
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFotoAEliminar(null)} disabled={eliminando} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={confirmarEliminar}
            disabled={eliminando}
            color="error"
            variant="contained"
            startIcon={<DeleteOutlineIcon />}
          >
            {eliminando ? "Eliminando…" : "Eliminar foto"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
